import { Injectable } from "@angular/core";
import { QuestionAnswerEntity, QuestionTypeAllItems, QuizEntity, QuizQuestionLinkEntity, QuizResultEntity, QuizResultQuestionEntity, QuizResultStatusAllItems } from "../../../meta-data/app-meta-data.service";
import { QuizDisplayDetailCard, QuizDisplayResult } from "../models/quiz-display-result.model";
import { DateTimeUtilService } from "../../shared/util/date-time-util.service";
import { QuestionAnswerReview, QuestionAnswerReviewResult, QuestionAnswerReviews } from "../models/question-answer-review.model";
import { QuestionAnswerValue } from "../models/question-answer-value.model";
import { QuizSetupResult } from "../models/quiz-setup-result.model";
import { QuizRepositoryService } from "./quiz-repository.service";
import { ArrayUtilService } from "../../shared/util/array-util.service";
import { Observable, of } from "rxjs";
import { DwSecurityStateService } from "@devwareapps/devware-cap";

@Injectable({ providedIn: 'root' })
export class QuizUtilService {
    constructor(private dateTimeUtilService: DateTimeUtilService,
        private quizResultService: QuizRepositoryService,
        private arrayUtilService: ArrayUtilService,
        private dwSecurityStateService: DwSecurityStateService
    ) { }




    getQuizDisplayResult(quiz: QuizEntity, quizResult: QuizResultEntity): QuizDisplayResult {
        let displayResult: QuizDisplayResult = {
            detailCards: []
        } as any;

        quizResult.QuizResultStatusId = quizResult.QuizResultStatusId || QuizResultStatusAllItems.Failed;

        switch (quizResult.QuizResultStatusId) {
            case QuizResultStatusAllItems.Incomplete:
                displayResult.headerText = 'Quiz Incomplete';
                displayResult.headerIconClass = "fa fa-circle-info";
                displayResult.statusClass = 'is-pending-review';

                return displayResult;

            case QuizResultStatusAllItems.CompleteUngraded:
                displayResult.headerText = 'Quiz Not Graded';
                displayResult.headerIconClass = "fa fa-circle-info";
                displayResult.statusClass = 'is-pending-review';

                return displayResult;

            case QuizResultStatusAllItems.Passed:
                displayResult.headerText = 'Quiz Passed';
                displayResult.headerIconClass = "fa fa-check-circle";
                displayResult.statusClass = 'is-passed';

                break;

            case QuizResultStatusAllItems.Failed:
                displayResult.headerText = 'Quiz Failed';
                displayResult.headerIconClass = "fa fa-times-circle";
                displayResult.statusClass = 'is-failed';

                break;
        }

        displayResult.detailCards = this.getQuizResultDetailCards(quiz, quizResult);

        return displayResult;
    }

    getQuizResultDetailCards(quiz: QuizEntity, quizResult: QuizResultEntity): QuizDisplayDetailCard[] {
        const cards: QuizDisplayDetailCard[] = [];

        cards.push({
            headerText: 'My Score',
            detail: this.formatPercentage(quizResult?.PercentageScore),
            notes: `Passing: ${quiz.PassingPercent}%`,

            iconClass: 'fa fa-bullseye'
        });


        let timeAllowed = 'None';
        if (quiz.TimeLimitMinutes) {
            timeAllowed = this.dateTimeUtilService.formatDurationMinutesSeconds(quiz.TimeLimitMinutes * 60);
        }

        cards.push({
            headerText: 'Time',
            detail: this.dateTimeUtilService.formatDurationMinutesSeconds(quizResult.TotalTimeSeconds),
            notes: `Limit: ${timeAllowed}`,
            iconClass: 'fa fa-clock'
        });

        const correctAnswers = quizResult.CorrectQuestions == null
            ? (quizResult.QuizResultQuestion || []).filter(q => q.IsCorrect).length
            : quizResult.CorrectQuestions;
        const completedQuestions = quizResult.TotalQuestions || quizResult.CompletedQuestions || 0;

        cards.push({
            headerText: 'Correct',
            detail: `${correctAnswers} / ${completedQuestions}`,
            notes: 'Questions: ' + quizResult.TotalQuestions,
            iconClass: 'fa fa-check-circle'
        });

        return cards;
    }

    private formatPercentage(percentageScore: number): string {
        return Number.isFinite(percentageScore) ? `${Math.round(percentageScore)}%` : '—';
    }


    getAnswerReviews(questionLink: QuizQuestionLinkEntity, questionResult: QuizResultQuestionEntity, questionAnswerValue: QuestionAnswerValue, answersToShow?: QuestionAnswerEntity[], testMode?: boolean): QuestionAnswerReviews {
        const result: QuestionAnswerReviewResult = {
            questionAnswerReviews: {},
            aiExplanationHtml: ''
        };

        if (!questionLink || !questionResult || !questionAnswerValue) {
            return result.questionAnswerReviews;
        }

        const question = questionLink.Question;

        switch (question.QuestionTypeId) {
            case QuestionTypeAllItems.TrueOrFalse:
                result.questionAnswerReviews = this.getAnswerNotesTrueFalse(questionLink, questionResult, questionAnswerValue, testMode);
                break;
            case QuestionTypeAllItems.MultipleAnswer:
            case QuestionTypeAllItems.MultipleChoice:
                result.questionAnswerReviews = this.getAnswerNotesMultiple(questionLink, questionResult, questionAnswerValue, answersToShow, testMode);
                break;
        } 

        return result.questionAnswerReviews;
    }

    getAnswerNotesTrueFalse(questionLink: QuizQuestionLinkEntity, questionResult: QuizResultQuestionEntity, questionAnswerValue: QuestionAnswerValue, testMode?: boolean): QuestionAnswerReviews {
        const question = questionLink.Question;

        const answerReviews: QuestionAnswerReviews = {};

        const selectedIndex = questionAnswerValue.trueFalseAnswer ? 0 : 1;
        const correctIndex = question.CorrectAnswer?.toLowerCase() === 'true' ? 0 : 1;

        answerReviews[selectedIndex] = {
            iconClass: questionResult.IsCorrect ? 'fa fa-check dw-green' : 'fa fa-times dw-red',
            textNote: questionResult.IsCorrect ? 'Correct' : 'Incorrect'
        };

        if (selectedIndex !== correctIndex) {
            answerReviews[correctIndex] = {
                iconClass: 'fa fa-check dw-green',
                textNote: 'Correct answer'
            };
        }

        return answerReviews;
    }


    getAnswerNotesMultiple(questionLink: QuizQuestionLinkEntity, questionResult: QuizResultQuestionEntity, questionAnswerValue: QuestionAnswerValue, answersToShow: QuestionAnswerEntity[],  testMode?: boolean): QuestionAnswerReviews {
        const question = questionLink.Question;

        const answerReviews: QuestionAnswerReviews = {};

        const answersWithDetail = questionAnswerValue.originalQuestion?.QuestionAnswer || [];


        for (const answer of answersToShow) {
            const answerWithDetail = answersWithDetail.find(a => a.QuestionAnswerId === answer.QuestionAnswerId) || answer;

            const isSelected = questionAnswerValue?.quizQuestionAnswerId == answer.QuestionAnswerId || questionAnswerValue?.quizQuestionAnswerIds?.includes(answer.QuestionAnswerId);

            if (!isSelected && !answerWithDetail?.IsCorrectAnswer) {
                continue;
            }

            const index = answersToShow.indexOf(answer);

            answerReviews[index] = {
                iconClass: answerWithDetail?.IsCorrectAnswer ? 'fa fa-check dw-green' : 'fa fa-times dw-red',
                textNote: answerWithDetail?.IsCorrectAnswer ? (isSelected ? 'Correct' : 'Correct answer') : 'Incorrect',
                explanationText: answerWithDetail?.ExplanationText || answer.ExplanationText,
                explanationTextClass: answerWithDetail?.IsCorrectAnswer ? 'dw-green' : 'dw-red',
                // Keep historical grading details, but allow explanations added after the attempt.
                aiExplanationHtml: answerWithDetail?.QuestionAnswerAIExplanation?.ExplanationHtml
                    || answer.QuestionAnswerAIExplanation?.ExplanationHtml
            };
        }

        return answerReviews;
    }
}
